import React, { useMemo } from "react";
import ListView from "#rscv/List/ListView";
import LegendItem from "./LegendItem";

// Make the Legend component generic
export interface LegendProps<T> {
  className?: string;
  itemClassName?: string;
  iconSelector?: (item: T) => React.ReactNode;
  keySelector: (item: T) => React.Key;
  labelSelector: (item: T) => string;
  colorSelector: (item: T) => string;
  radiusSelector?: (item: T) => number | undefined;
  valueSelector?: (item: T) => number;
  emptyComponent?: React.ReactNode;
  symbolClassNameSelector?: (item: T) => string;
  data?: readonly T[];
}

function Legend<T>({
  className,
  itemClassName,
  iconSelector,
  keySelector,
  labelSelector,
  colorSelector,
  radiusSelector,
  valueSelector,
  emptyComponent,
  symbolClassNameSelector,
  data = [],
}: LegendProps<T>) {
  // Filter data directly in useMemo instead of using memoizeOne
  const filteredData = useMemo(() => {
    if (!valueSelector) return [...data];
    return data.filter((item) => valueSelector(item) > 0);
  }, [data, valueSelector]);

  const rendererParams = useMemo(() => {
    if (
      !radiusSelector &&
      !symbolClassNameSelector &&
      !iconSelector &&
      !itemClassName
    ) {
      return undefined; // ← don't pass if not needed
    }

    const allRadii = radiusSelector ? data.map(radiusSelector) : [];
    const validRadii = allRadii.filter(
      (r): r is number => typeof r === "number"
    );
    const maxRadius =
      validRadii.length > 0 ? Math.max(...validRadii) : undefined;

    return (_key: React.Key, item: T) => ({
      icon: iconSelector?.(item),
      label: labelSelector(item),
      color: colorSelector(item),
      radius: radiusSelector?.(item),
      maxRadius,
      className: itemClassName,
      symbolClassName: symbolClassNameSelector?.(item) ?? "",
    });
  }, [
    iconSelector,
    labelSelector,
    colorSelector,
    radiusSelector,
    itemClassName,
    symbolClassNameSelector,
    data,
  ]);

  return (
    <ListView
      className={className}
      data={filteredData}
      renderer={LegendItem}
      keySelector={keySelector}
      rendererParams={rendererParams}
      emptyComponent={emptyComponent ?? undefined}
    />
  );
}

export default Legend;
